/**
 * Governance Workspace Page - Sprint 14
 * GC-1: Stage-Gate Governance Board
 * 
 * 5-stage board: Intake → Assessment → Governance → Approved → Rejected
 * With SLA tracking, filters, drag-and-drop, and approval management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, XCircle, Clock, AlertTriangle, Filter, RefreshCw,
  ChevronRight, Plus, User, Calendar, FileText, DollarSign,
  Shield, Users, Building, ArrowRight, AlertCircle, Loader2,
  GripVertical, Tag, Search, ExternalLink, History, X
} from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  return headers;
};

// Stage configuration
const STAGES = [
  { id: 'intake_review', name: 'Intake Review', color: 'bg-blue-500', icon: FileText },
  { id: 'assessment_review', name: 'Assessment', color: 'bg-amber-500', icon: Shield },
  { id: 'governance_board', name: 'Governance Board', color: 'bg-purple-500', icon: Users },
  { id: 'approved', name: 'Approved', color: 'bg-green-500', icon: CheckCircle },
  { id: 'rejected', name: 'Rejected', color: 'bg-red-500', icon: XCircle }
];

// SLA Badge Component
const SLABadge = ({ status, deadline }) => {
  const config = {
    on_track: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'On Track' },
    at_risk: { color: 'bg-amber-100 text-amber-700', icon: AlertTriangle, label: 'At Risk' },
    breached: { color: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'SLA Breached' }
  };
  
  const cfg = config[status] || config.on_track;
  const Icon = cfg.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

// Draggable Governance Item Card
const DraggableCard = ({ item, onViewDetails, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging: isCurrentlyDragging
  } = useDraggable({
    id: item.item_id,
    data: { item }
  });
  
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isCurrentlyDragging ? 0.5 : 1,
  } : undefined;
  
  const canDrag = item.current_stage !== 'approved' && item.current_stage !== 'rejected';
  
  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg border shadow-sm p-3 mb-2 hover:shadow-md transition-all cursor-pointer ${
        isCurrentlyDragging ? 'ring-2 ring-blue-400 shadow-lg' : ''
      }`}
      onClick={() => onViewDetails(item)}
      data-testid={`governance-card-${item.item_id}`}
    >
      <div className="flex items-start gap-2 mb-2">
        {/* Drag Handle */}
        {canDrag && (
          <div 
            {...listeners} 
            {...attributes}
            className="cursor-grab active:cursor-grabbing p-1 -ml-1 hover:bg-gray-100 rounded"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 text-sm truncate">{item.title}</h4>
              <p className="text-xs text-gray-500 truncate">{item.reference_id}</p>
            </div>
            <span className={`text-xs px-1.5 py-0.5 rounded ml-2 flex-shrink-0 ${
              item.item_type === 'demand' ? 'bg-blue-100 text-blue-700' :
              item.item_type === 'scope' ? 'bg-purple-100 text-purple-700' :
              item.item_type === 'decision' ? 'bg-amber-100 text-amber-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {item.item_type}
            </span>
          </div>
        </div>
      </div>
      
      {/* Meta info */}
      <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
        {item.submitter_name && (
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {item.submitter_name}
          </span>
        )}
        {item.stage_entered_at && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {new Date(item.stage_entered_at).toLocaleDateString()}
          </span>
        )}
      </div>
      
      {/* Indicators */}
      <div className="flex items-center gap-2 flex-wrap">
        {item.sla_status && item.current_stage !== 'approved' && item.current_stage !== 'rejected' && (
          <SLABadge status={item.sla_status} deadline={item.sla_deadline} />
        )}
        
        {item.financial_estimate_present && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700">
            <DollarSign className="w-3 h-3" />
            Financial
          </span>
        )}
        
        {item.risk_assessment_complete && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700">
            <Shield className="w-3 h-3" />
            Risk
          </span>
        )}
      </div>
      
      {/* Approvals */}
      {item.approvals && item.approvals.length > 0 && (
        <div className="mt-2 pt-2 border-t">
          <div className="flex items-center gap-1 flex-wrap">
            {item.approvals.filter(a => a.action === 'approve').map((a, idx) => (
              <span key={idx} className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                {a.role}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Quick Links */}
      <div className="mt-2 pt-2 border-t flex items-center gap-3">
        {item.portfolio_id && (
          <a
            href={`/portfolio/hierarchy?select=${item.portfolio_id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            data-testid={`view-portfolio-${item.item_id}`}
          >
            <ExternalLink className="w-3 h-3" />
            View in Portfolio
          </a>
        )}
        {item.item_type === 'demand' && (
          <a
            href={`/demands/${item.reference_id}?tab=history`}
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
            data-testid={`view-history-${item.item_id}`}
          >
            <History className="w-3 h-3" />
            History Timeline
          </a>
        )}
      </div>
    </div>
  );
};

// Drag Overlay Card (displayed while dragging)
const DragOverlayCard = ({ item }) => {
  if (!item) return null;
  
  return (
    <div className="bg-white rounded-lg border-2 border-blue-400 shadow-xl p-3 w-[280px] opacity-90">
      <div className="flex items-start gap-2">
        <GripVertical className="w-4 h-4 text-blue-400" />
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 text-sm truncate">{item.title}</h4>
          <p className="text-xs text-gray-500 truncate">{item.reference_id}</p>
        </div>
      </div>
    </div>
  );
};

// Droppable Stage Column Component
const StageColumn = ({ stage, items, onViewDetails, onAddItem, isOver }) => {
  const { setNodeRef } = useDroppable({
    id: stage.id,
    data: { stage }
  });
  
  const Icon = stage.icon;
  const isTerminal = stage.id === 'approved' || stage.id === 'rejected';
  
  return (
    <div 
      ref={setNodeRef}
      className={`flex-1 min-w-[280px] max-w-[320px] bg-gray-50 rounded-lg flex flex-col transition-all ${
        isOver && !isTerminal ? 'ring-2 ring-blue-400 bg-blue-50/50' : ''
      }`}
    >
      {/* Header */}
      <div className={`${stage.color} text-white px-3 py-2 rounded-t-lg flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <span className="font-medium text-sm">{stage.name}</span>
        </div>
        <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs font-semibold">
          {items.length}
        </span>
      </div>
      
      {/* Drop zone indicator */}
      {isOver && !isTerminal && (
        <div className="mx-2 mt-2 p-2 border-2 border-dashed border-blue-400 rounded-lg bg-blue-50 text-center text-xs text-blue-600">
          Drop here to move
        </div>
      )}
      
      {/* Items */}
      <div className="p-2 flex-1 overflow-y-auto max-h-[calc(100vh-280px)]">
        {items.map(item => (
          <DraggableCard
            key={item.item_id}
            item={item}
            onViewDetails={onViewDetails}
          />
        ))}
        
        {items.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">
            No items in this stage
          </div>
        )}
      </div>
      
      {/* Add button for intake only */}
      {stage.id === 'intake_review' && (
        <div className="p-2 border-t">
          <button
            onClick={onAddItem}
            className="w-full flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 border-2 border-dashed rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors"
            data-testid="add-governance-item-btn"
          >
            <Plus className="w-4 h-4" />
            Add Item
          </button>
        </div>
      )}
    </div>
  );
};

// Item Details Drawer
const ItemDetailsDrawer = ({ item, isOpen, onClose, onApprove, onReject, stageConfig }) => {
  const [approving, setApproving] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [notes, setNotes] = useState('');
  
  if (!isOpen || !item) return null;
  
  const requiredApprovers = stageConfig?.required_approvers || [];
  const receivedApprovals = item.approvals?.filter(a => a.action === 'approve').map(a => a.role) || [];
  const pendingApprovers = requiredApprovers.filter(r => !receivedApprovals.includes(r));
  
  const handleApprove = async () => {
    if (!selectedRole) {
      toast.error('Please select an approval role');
      return;
    }
    
    setApproving(true);
    try {
      await onApprove(item.item_id, selectedRole, notes);
      toast.success(`${selectedRole} approval recorded`);
      setSelectedRole('');
      setNotes('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between bg-gray-50">
          <h2 className="font-semibold text-lg">Item Details</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Basic Info */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
            <p className="text-sm text-gray-500">{item.reference_id} • {item.item_type}</p>
          </div>
          
          {item.description && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Description</label>
              <p className="text-sm text-gray-700 mt-1">{item.description}</p>
            </div>
          )}
          
          {/* Stage Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <label className="text-xs font-medium text-gray-500 uppercase">Current Stage</label>
            <p className="text-sm font-medium text-gray-900 mt-1">
              {item.current_stage?.replace('_', ' ')}
            </p>
            {item.sla_deadline && (
              <p className="text-xs text-gray-500 mt-1">
                SLA Deadline: {new Date(item.sla_deadline).toLocaleString()}
              </p>
            )}
          </div>
          
          {/* Entry/Exit Criteria */}
          {stageConfig && (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 rounded-lg p-3">
                <label className="text-xs font-medium text-blue-700 uppercase">Entry Criteria</label>
                <ul className="text-xs text-blue-900 mt-1 space-y-1">
                  {stageConfig.entry_criteria?.map((c, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <CheckCircle className="w-3 h-3 mt-0.5 text-green-500" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <label className="text-xs font-medium text-amber-700 uppercase">Exit Criteria</label>
                <ul className="text-xs text-amber-900 mt-1 space-y-1">
                  {stageConfig.exit_criteria?.map((c, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <Clock className="w-3 h-3 mt-0.5" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {/* Approvals Status */}
          {requiredApprovers.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Required Approvals</label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {requiredApprovers.map(role => (
                  <span 
                    key={role}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      receivedApprovals.includes(role) 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {receivedApprovals.includes(role) && <CheckCircle className="w-3 h-3 inline mr-1" />}
                    {role}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Approval History */}
          {item.history && item.history.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">History</label>
              <div className="mt-2 space-y-2">
                {item.history.slice().reverse().slice(0, 5).map((h, idx) => (
                  <div key={idx} className="text-xs bg-gray-50 rounded p-2">
                    <span className="font-medium">{h.action}</span>
                    {h.role && <span className="text-gray-500"> by {h.role}</span>}
                    <span className="text-gray-400 ml-2">
                      {new Date(h.timestamp).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Approval Action */}
          {pendingApprovers.length > 0 && item.current_stage !== 'approved' && item.current_stage !== 'rejected' && (
            <div className="border-t pt-4">
              <label className="text-xs font-medium text-gray-500 uppercase mb-2 block">Record Approval</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm mb-2"
              >
                <option value="">Select role...</option>
                {pendingApprovers.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Approval notes (optional)"
                className="w-full border rounded px-3 py-2 text-sm mb-2"
                rows={2}
              />
              <button
                onClick={handleApprove}
                disabled={approving || !selectedRole}
                className="w-full py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Approve
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Add Item Modal
const AddItemModal = ({ isOpen, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    item_type: 'demand',
    reference_id: '',
    title: '',
    description: '',
    project_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  
  useEffect(() => {
    if (isOpen) {
      axios.get(`${API_BASE}/projects?limit=50`, { headers: getAuthHeaders() })
        .then(res => setProjects(res.data.projects || []))
        .catch(() => {});
    }
  }, [isOpen]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.reference_id) {
      toast.error('Title and Reference ID are required');
      return;
    }
    
    setSubmitting(true);
    try {
      await onSubmit(form);
      toast.success('Item added to governance workflow');
      onClose();
      setForm({ item_type: 'demand', reference_id: '', title: '', description: '', project_id: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add item');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4">Add to Governance Workflow</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Item Type</label>
            <select
              value={form.item_type}
              onChange={(e) => setForm({ ...form, item_type: e.target.value })}
              className="w-full border rounded px-3 py-2 mt-1"
            >
              <option value="demand">Demand</option>
              <option value="scope">Scope Change</option>
              <option value="decision">Decision</option>
              <option value="project">Project</option>
            </select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Reference ID *</label>
            <input
              type="text"
              value={form.reference_id}
              onChange={(e) => setForm({ ...form, reference_id: e.target.value })}
              placeholder="e.g., DEM-001, SCO-042"
              className="w-full border rounded px-3 py-2 mt-1"
              required
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Brief title"
              className="w-full border rounded px-3 py-2 mt-1"
              required
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional description"
              className="w-full border rounded px-3 py-2 mt-1"
              rows={3}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Project</label>
            <select
              value={form.project_id}
              onChange={(e) => setForm({ ...form, project_id: e.target.value })}
              className="w-full border rounded px-3 py-2 mt-1"
            >
              <option value="">Select project...</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border rounded font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Add Item
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Compliance Pack Modal
const CompliancePackModal = ({ isOpen, onClose, projects }) => {
  const [format, setFormat] = useState('zip');
  const [mode, setMode] = useState('full');
  const [branding, setBranding] = useState('default');
  const [projectId, setProjectId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [preview, setPreview] = useState(null);
  
  // Load preview when filters change
  useEffect(() => {
    if (isOpen) {
      loadPreview();
    }
  }, [isOpen, projectId]);
  
  const loadPreview = async () => {
    try {
      const params = new URLSearchParams();
      if (projectId) params.append('projectId', projectId);
      
      const res = await axios.get(
        `${API_BASE}/governance/compliance-pack/preview?${params}`,
        { headers: getAuthHeaders() }
      );
      setPreview(res.data.preview);
    } catch (err) {
      console.error('Failed to load preview:', err);
    }
  };
  
  const handleGenerate = async () => {
    setGenerating(true);
    setProgress(0);
    setDownloadUrl(null);
    
    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(p => Math.min(p + 15, 90));
      }, 300);
      
      const params = new URLSearchParams();
      if (projectId) params.append('projectId', projectId);
      params.append('format', format);
      params.append('mode', mode);
      params.append('branding', branding);
      
      const res = await axios.get(
        `${API_BASE}/governance/compliance-pack?${params}`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      clearInterval(progressInterval);
      setProgress(100);
      
      // Create download URL
      const blob = new Blob([res.data], { 
        type: format === 'zip' ? 'application/zip' : 'text/html' 
      });
      const url = window.URL.createObjectURL(blob);
      setDownloadUrl(url);
      
      toast.success('Compliance pack generated successfully');
    } catch (err) {
      toast.error('Failed to generate compliance pack');
    } finally {
      setGenerating(false);
    }
  };
  
  const handleDownload = () => {
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `compliance_pack.${format === 'zip' ? 'zip' : 'html'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="compliance-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Generate Compliance Pack</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Preview */}
        {preview && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
            <h3 className="font-medium text-gray-700 mb-2">Pack Contents Preview</h3>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-white rounded border">
                <div className="text-lg font-bold text-blue-600">{preview.approvals_count}</div>
                <div className="text-xs text-gray-500">Approvals</div>
              </div>
              <div className="p-2 bg-white rounded border">
                <div className="text-lg font-bold text-purple-600">{preview.decisions_count}</div>
                <div className="text-xs text-gray-500">Decisions</div>
              </div>
              <div className="p-2 bg-white rounded border">
                <div className="text-lg font-bold text-green-600">{preview.baselines_count}</div>
                <div className="text-xs text-gray-500">Baselines</div>
              </div>
            </div>
          </div>
        )}
        
        {/* Format Selection */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Export Format</label>
            <div className="flex gap-3 mt-2">
              <button
                type="button"
                onClick={() => setFormat('zip')}
                className={`flex-1 py-2 px-4 rounded-lg border ${
                  format === 'zip' 
                    ? 'bg-blue-50 border-blue-300 text-blue-700' 
                    : 'hover:bg-gray-50'
                }`}
                data-testid="format-zip"
              >
                <FileText className="w-5 h-5 mx-auto mb-1" />
                <div className="text-sm font-medium">Full ZIP</div>
                <div className="text-xs text-gray-500">All reports + JSON</div>
              </button>
              <button
                type="button"
                onClick={() => { setFormat('pdf'); setMode('summary'); }}
                className={`flex-1 py-2 px-4 rounded-lg border ${
                  format === 'pdf' 
                    ? 'bg-blue-50 border-blue-300 text-blue-700' 
                    : 'hover:bg-gray-50'
                }`}
                data-testid="format-pdf"
              >
                <FileText className="w-5 h-5 mx-auto mb-1" />
                <div className="text-sm font-medium">Summary PDF</div>
                <div className="text-xs text-gray-500">KPIs + overview</div>
              </button>
            </div>
          </div>
          
          {/* Branding */}
          <div>
            <label className="text-sm font-medium text-gray-700">Branding</label>
            <div className="flex items-center gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="branding"
                  checked={branding === 'default'}
                  onChange={() => setBranding('default')}
                  className="text-blue-600"
                />
                <span className="text-sm">Include watermark</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="branding"
                  checked={branding === 'none'}
                  onChange={() => setBranding('none')}
                  className="text-blue-600"
                />
                <span className="text-sm">No branding</span>
              </label>
            </div>
          </div>
          
          {/* Filter */}
          <div>
            <label className="text-sm font-medium text-gray-700">Filter by Project</label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full border rounded px-3 py-2 mt-1"
            >
              <option value="">All Projects</option>
              {projects?.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          
          {/* Progress */}
          {generating && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                <span>Generating...</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
          
          {/* Download Button */}
          {downloadUrl && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Pack ready!</span>
              </div>
              <button
                onClick={handleDownload}
                className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                data-testid="download-pack-btn"
              >
                Download
              </button>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex-1 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="generate-pack-btn"
            >
              {generating && <Loader2 className="w-4 h-4 animate-spin" />}
              Generate Pack
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const GovernanceWorkspace = () => {
  const navigate = useNavigate();
  const [board, setBoard] = useState({});
  const [stageCounts, setStageCounts] = useState({});
  const [stageDefinitions, setStageDefinitions] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ 
    item_type: '', 
    project_id: '',
    owner: '',
    priority: '',
    dateRange: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [slaSummary, setSlaSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [activeItem, setActiveItem] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Load board data
  const loadBoard = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.item_type) params.append('item_type', filters.item_type);
      if (filters.project_id) params.append('project_id', filters.project_id);
      
      const [boardRes, stagesRes, slaRes, projectsRes] = await Promise.all([
        axios.get(`${API_BASE}/governance/board?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/governance/stages`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/governance/sla/summary`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/projects?limit=50`, { headers: getAuthHeaders() })
      ]);
      
      setBoard(boardRes.data.board || {});
      setStageCounts(boardRes.data.stage_counts || {});
      setProjects(projectsRes.data.projects || []);
      
      // Build stage definitions map
      const defMap = {};
      (stagesRes.data.stages || []).forEach(s => {
        defMap[s.stage_id] = s;
      });
      setStageDefinitions(defMap);
      
      setSlaSummary(slaRes.data.summary);
    } catch (err) {
      console.error('Failed to load governance board:', err);
      toast.error('Failed to load governance board');
    } finally {
      setLoading(false);
    }
  }, [filters]);
  
  useEffect(() => {
    loadBoard();
  }, [loadBoard]);
  
  // Drag and Drop handlers
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveItem(active.data.current?.item);
  };
  
  const handleDragOver = (event) => {
    const { over } = event;
    if (over) {
      setOverStage(over.id);
    } else {
      setOverStage(null);
    }
  };
  
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveItem(null);
    setOverStage(null);
    
    if (!over || !active) return;
    
    const item = active.data.current?.item;
    const targetStage = over.id;
    
    if (!item || item.current_stage === targetStage) return;
    
    // Prevent dropping into terminal stages via drag
    if (targetStage === 'approved' || targetStage === 'rejected') {
      toast.error('Cannot drag items to terminal stages. Use the details panel to approve/reject.');
      return;
    }
    
    // Validate stage progression
    const stageOrder = ['intake_review', 'assessment_review', 'governance_board'];
    const currentIdx = stageOrder.indexOf(item.current_stage);
    const targetIdx = stageOrder.indexOf(targetStage);
    
    if (targetIdx <= currentIdx) {
      toast.error('Cannot move backward in the workflow');
      return;
    }
    
    // Attempt transition
    try {
      await handleTransition(item.item_id, targetStage);
      toast.success(`Moved to ${targetStage.replace(/_/g, ' ')}`);
    } catch (err) {
      // Error already handled in handleTransition
    }
  };
  
  const handleDragCancel = () => {
    setActiveItem(null);
    setOverStage(null);
  };
  
  // Transition item
  const handleTransition = async (itemId, targetStage) => {
    await axios.post(
      `${API_BASE}/governance/items/${itemId}/transition`,
      { target_stage: targetStage },
      { headers: getAuthHeaders() }
    );
    loadBoard();
  };
  
  // Approve item
  const handleApprove = async (itemId, role, notes) => {
    await axios.post(
      `${API_BASE}/governance/items/${itemId}/approve`,
      { action: 'approve', role, notes },
      { headers: getAuthHeaders() }
    );
    // Refresh item details
    const res = await axios.get(`${API_BASE}/governance/items/${itemId}`, { headers: getAuthHeaders() });
    setSelectedItem(res.data.item);
    loadBoard();
  };
  
  // Add new item
  const handleAddItem = async (form) => {
    await axios.post(`${API_BASE}/governance/items`, form, { headers: getAuthHeaders() });
    loadBoard();
  };
  
  // View item details
  const handleViewDetails = async (item) => {
    try {
      const res = await axios.get(`${API_BASE}/governance/items/${item.item_id}`, { headers: getAuthHeaders() });
      setSelectedItem(res.data.item);
    } catch (err) {
      toast.error('Failed to load item details');
    }
  };
  
  // Filter items by search query
  const filterItems = (items) => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item => 
      item.title?.toLowerCase().includes(query) ||
      item.reference_id?.toLowerCase().includes(query) ||
      item.submitter_name?.toLowerCase().includes(query)
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="min-h-screen bg-gray-100" data-testid="governance-workspace">
        {/* Header */}
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Governance Workspace</h1>
              <p className="text-sm text-gray-500">Stage-gate workflow for demands, scopes, and decisions</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search items..."
                  className="pl-9 pr-4 py-2 border rounded-lg text-sm w-64"
                  data-testid="governance-search"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm ${showFilters ? 'bg-blue-50 border-blue-300' : ''}`}
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <button
                onClick={() => setShowComplianceModal(true)}
                className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50 bg-purple-50 border-purple-200 text-purple-700"
                data-testid="compliance-pack-btn"
              >
                <FileText className="w-4 h-4" />
                Compliance Pack
              </button>
              <button
                onClick={loadBoard}
                className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
                data-testid="refresh-board-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
          
          {/* SLA Summary */}
          {slaSummary && (
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-500">SLA Status:</span>
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle className="w-4 h-4" />
                {slaSummary.on_track} On Track
              </span>
              <span className="flex items-center gap-1 text-amber-600">
                <AlertTriangle className="w-4 h-4" />
                {slaSummary.at_risk} At Risk
              </span>
              <span className="flex items-center gap-1 text-red-600">
                <AlertCircle className="w-4 h-4" />
                {slaSummary.breached} Breached
              </span>
            </div>
          )}
          
          {/* Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t flex gap-4 flex-wrap">
              <select
                value={filters.item_type}
                onChange={(e) => setFilters({ ...filters, item_type: e.target.value })}
                className="border rounded px-3 py-2 text-sm"
                data-testid="filter-type"
              >
                <option value="">All Types</option>
                <option value="demand">Demands</option>
                <option value="scope">Scope Changes</option>
                <option value="decision">Decisions</option>
                <option value="project">Projects</option>
              </select>
              
              <select
                value={filters.project_id}
                onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}
                className="border rounded px-3 py-2 text-sm"
                data-testid="filter-project"
              >
                <option value="">All Projects</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              
              <select
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                className="border rounded px-3 py-2 text-sm"
                data-testid="filter-priority"
              >
                <option value="">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              
              <select
                value={filters.dateRange}
                onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                className="border rounded px-3 py-2 text-sm"
                data-testid="filter-date"
              >
                <option value="">Any Date</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
              
              {(filters.item_type || filters.project_id || filters.priority || filters.dateRange) && (
                <button
                  onClick={() => setFilters({ item_type: '', project_id: '', owner: '', priority: '', dateRange: '' })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Board */}
        <div className="p-6">
          <div className="flex gap-4 overflow-x-auto pb-4">
            {STAGES.map(stage => (
              <StageColumn
                key={stage.id}
                stage={stage}
                items={filterItems(board[stage.id]?.items || [])}
                onViewDetails={handleViewDetails}
                onAddItem={() => setShowAddModal(true)}
                isOver={overStage === stage.id}
              />
            ))}
          </div>
        </div>
        
        {/* Drag Overlay */}
        <DragOverlay>
          {activeItem && <DragOverlayCard item={activeItem} />}
        </DragOverlay>
        
        {/* Item Details Drawer */}
        <ItemDetailsDrawer
          item={selectedItem}
          isOpen={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          onApprove={handleApprove}
          stageConfig={selectedItem ? stageDefinitions[selectedItem.current_stage] : null}
        />
        
        {/* Add Item Modal */}
        <AddItemModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddItem}
        />
        
        {/* Compliance Pack Modal */}
        <CompliancePackModal
          isOpen={showComplianceModal}
          onClose={() => setShowComplianceModal(false)}
          projects={projects}
        />
      </div>
    </DndContext>
  );
};

export default GovernanceWorkspace;
